import { formatMoney } from '@insify/global-money';
import { useTranslation } from 'react-i18next';

import { useConditionsQuery } from '../../hooks/useQueries';

import type { PropertyMultiRiskKey } from '../../models/coverage';
import type { MoneyField } from '@insify/global-money';

type CoverageOptions = {
  professionalLiabilityOptions: {
    label: string;
    value: MoneyField;
    hint?: string;
  }[];
  publicLiabilityOptions: {
    enabled: {
      coverLimit: MoneyField;
    };
    disabled: {
      coverLimit: MoneyField;
    };
  };
  legalProtectionOptions: {
    enabled: {
      coverLimit: MoneyField;
    };
    disabled: {
      coverLimit: MoneyField;
    };
  };
  deductibleOptions: {
    label: string;
    value: MoneyField;
    hint?: string;
  }[];
  propertyCoverageOptions: {
    [key in PropertyMultiRiskKey]?: { startingPremium: MoneyField; options: MoneyField[] };
  };
};

export const useCoverageOptions = (): CoverageOptions => {
  const { t, i18n } = useTranslation(['funnel']);
  const { data } = useConditionsQuery();

  return {
    professionalLiabilityOptions:
      data?.professionalLiability?.coverLimitOptions.map((value) => {
        const option: { label: string; value: MoneyField; hint?: string } = {
          value,
          label: formatMoney(value, i18n.language, { withoutFraction: true }),
        };
        if (value.cents === data?.professionalLiability?.popularCoverLimitOption.cents)
          option.hint = t('funnel:coverage_recommended_label');
        return option;
      }) || [],
    publicLiabilityOptions: {
      enabled: {
        coverLimit: { currency: 'EUR', cents: data?.riskCarriers?.[0] === 'hiscox' ? '1000000000' : '500000000' },
      },
      disabled: { coverLimit: { currency: 'EUR', cents: '0' } },
    } as const,
    legalProtectionOptions: {
      enabled: {
        coverLimit: { currency: 'EUR', cents: '5000000' }, // Always the same cover limit, not visible when disabled
      },
      disabled: {
        coverLimit: { currency: 'EUR', cents: '0' },
      },
    },
    deductibleOptions:
      data?.deductibleOptions?.map((value) => {
        const option: { label: string; value: MoneyField; hint?: string } = {
          value,
          label: formatMoney(value, i18n.language, { withoutFraction: true }),
        };
        if (value.cents === data?.popularDeductibleOption?.cents) option.hint = t('funnel:coverage_recommended_label');
        return option;
      }) || [],
    propertyCoverageOptions: {
      electronics: {
        startingPremium: { cents: '836', currency: 'EUR' },
        options: [
          { cents: '0', currency: 'EUR' },
          { cents: '500000', currency: 'EUR' },
          { cents: '1000000', currency: 'EUR' },
          { cents: '1500000', currency: 'EUR' },
          { cents: '2000000', currency: 'EUR' },
          { cents: '2500000', currency: 'EUR' },
        ],
      },
      inventory: {
        startingPremium: { cents: '479', currency: 'EUR' },
        options: [
          { cents: '0', currency: 'EUR' },
          { cents: '500000', currency: 'EUR' },
          { cents: '1000000', currency: 'EUR' },
          { cents: '1500000', currency: 'EUR' },
          { cents: '2000000', currency: 'EUR' },
          { cents: '2500000', currency: 'EUR' },
          { cents: '3000000', currency: 'EUR' },
          { cents: '3500000', currency: 'EUR' },
          { cents: '4000000', currency: 'EUR' },
          { cents: '4500000', currency: 'EUR' },
          { cents: '5000000', currency: 'EUR' },
        ],
      },
      stock: {
        startingPremium: { cents: '548', currency: 'EUR' },
        options: [
          { cents: '0', currency: 'EUR' },
          { cents: '1000000', currency: 'EUR' },
          { cents: '2000000', currency: 'EUR' },
          { cents: '3000000', currency: 'EUR' },
          { cents: '4000000', currency: 'EUR' },
          { cents: '5000000', currency: 'EUR' },
        ],
      },
      tools: {
        startingPremium: { cents: '1136', currency: 'EUR' },
        options: [
          { cents: '0', currency: 'EUR' },
          { cents: '500000', currency: 'EUR' },
          { cents: '1000000', currency: 'EUR' },
        ],
      },
    },
  };
};
